/**
 * useAuth Hook
 * Provides authentication state and methods throughout the app
 */

import { useState, useEffect, useCallback } from 'react';
import authService, { AUTH_CHANGE_EVENT, authSessionStore } from '@modules/auth/services/authService';
import { syncTimeZone } from '@modules/auth/services/timezoneSync';
import type { User } from '@/types/models';

/** How long before its access token expires a remembered session is renewed. */
const REMEMBERED_RENEW_AHEAD_MS = 5 * 60 * 1000;

interface UseAuthReturn {
  user: User | null;
  token: string | null;
  tenantId: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  isSuperAdmin: boolean;
  isGroupAdmin: boolean;
  isAdmin: boolean;
  login: (email: string, password: string, tenant?: string) => Promise<boolean>;
  logout: () => Promise<void>;
  hasRole: (role: 'super_admin' | 'group_admin' | 'admin' | 'user') => boolean;
  updateUser: (updates: Partial<User>) => void;
}

export function useAuth(): UseAuthReturn {
  // Initialize state from localStorage synchronously to avoid flash of unauthorized content
  const [user, setUser] = useState<User | null>(() => authService.getUser());
  const [token, setToken] = useState<string | null>(() => authService.getToken());
  const [tenantId, setTenantId] = useState<string | null>(() => authService.getTenantId());
  // If we already have a stored user, render as logged-in immediately.
  // The background verifyToken still runs to validate, but we don't gate
  // UI on it — otherwise every component that calls useAuth() flashes its
  // subtree to null on mount while it re-verifies (which blanks RequireFeature
  // and any other auth-gated tree on every route change).
  const [isLoading, setIsLoading] = useState(() => !authService.getUser());

  // Initialize auth state from localStorage AND validate with backend.
  // Every renewal / end below goes through authService, which emits
  // AUTH_CHANGE_EVENT — the listener further down re-reads the state.
  useEffect(() => {
    const initAuth = async () => {
      const storedToken = authService.getToken();
      const storedUser = authService.getUser();
      const storedTenantId = authService.getTenantId();

      // No session in this tab. A "Remember me" sign-in in this browser picks
      // it up (KEEPS isLoading=true until done, so route guards wait instead
      // of bouncing to the login page).
      if (!storedToken || !storedUser) {
        await authService.restoreRememberedSession();
        setIsLoading(false);
        return;
      }

      // Quick client-side check first: the token's own expiry.
      if (authService.getSessionExpiresAt() <= Date.now()) {
        console.log('[AUTH] Session expired locally');
        if (!(await authService.refreshTabSession())) {
          authService.endTabSession();
        }
        setIsLoading(false);
        return;
      }

      // Validate token with backend (KEEPS isLoading=true until complete)
      try {
        const isValid = await authService.verifyToken();

        if (isValid) {
          // Token valid - set auth state (re-read user in case verifyToken updated it)
          setUser(authService.getUser());
          setToken(storedToken);
          setTenantId(storedTenantId);
        } else if (!(await authService.refreshTabSession())) {
          // Token invalid and nothing remembered to renew it from.
          console.log('[AUTH] Token invalid, clearing auth');
          authService.endTabSession();
        }
      } catch (error) {
        console.error('[AUTH] Verification error:', error);
        // On error, fall back to local validation (already done above)
        setUser(storedUser);
        setToken(storedToken);
        setTenantId(storedTenantId);
      }

      setIsLoading(false);
    };

    initAuth();
  }, []);

  // Login method. Note: this wrapper does NOT handle the
  // `pick_tenant` outcome — the caller (only LoginPage in practice
  // calls authService directly anyway) should drive that flow itself.
  // If the response is a picker, this returns false because there's
  // no single user to set.
  const login = useCallback(
    async (email: string, password: string, tenant: string = 'test'): Promise<boolean> => {
      setIsLoading(true);
      try {
        const response = await authService.login(email, password, tenant);

        if ('access_token' in response && response.success) {
          setUser(response.user);
          setToken(response.access_token);
          setTenantId(response.user.tenant_id);
          return true;
        }

        return false;
      } catch (error) {
        console.error('Login error:', error);
        return false;
      } finally {
        setIsLoading(false);
      }
    },
    []
  );

  // Logout method. The redirect lands on the user's OWN branded login — a
  // super_admin viewing /team-alpha/... goes to their own tenant's login —
  // so the user is captured before the session is cleared.
  const logout = useCallback(async () => {
    await authService.logout();
    setUser(null);
    setToken(null);
    setTenantId(null);
    authService.redirectToOwnLogin(user);
  }, [user]);

  // In-tab sync: every component that calls useAuth() has its own
  // useState. When one instance triggers a login/logout via authService
  // (LoginPage uses authService.login directly), other instances in
  // the SAME tab would otherwise stay stale. authService dispatches
  // AUTH_CHANGE_EVENT on every auth-state mutation; we listen and
  // re-read from sessionStorage so all instances stay coherent.
  // We intentionally do NOT listen to the cross-tab `storage` event —
  // each tab holds its own independent session (multi-tenant support:
  // tab A logged into tenant-A, tab B logged into tenant-B, both
  // working simultaneously).
  useEffect(() => {
    function resync() {
      setUser(authService.getUser());
      setToken(authService.getToken());
      setTenantId(authService.getTenantId());
    }
    window.addEventListener(AUTH_CHANGE_EVENT, resync);
    return () => {
      window.removeEventListener(AUTH_CHANGE_EVENT, resync);
    };
  }, []);

  // A tab that comes back into view (or regains focus) re-reads the signed-in
  // user from the server: something may have changed while it sat open — the
  // organization renamed, its brand edited. A changed user notifies every
  // useAuth through AUTH_CHANGE_EVENT; verifyToken shares one request among all
  // instances and answers from its few-seconds cache, so this costs one call.
  useEffect(() => {
    const recheck = () => {
      if (document.visibilityState === 'visible' && authService.getToken()) void authService.verifyToken();
    };
    document.addEventListener('visibilitychange', recheck);
    window.addEventListener('focus', recheck);
    return () => {
      document.removeEventListener('visibilitychange', recheck);
      window.removeEventListener('focus', recheck);
    };
  }, []);

  // The agents date a turn by the person's own time zone: save the
  // browser's on the user, once per tab and zone (timezoneSync).
  const userId = user?.user_id;
  useEffect(() => {
    if (token) syncTimeZone(userId);
  }, [userId, token]);

  // Session expiry. A remembered session is renewed a little BEFORE its access
  // token expires, so an open tab never drops mid-work; any other session ends
  // at expiry. Timers do not run while a laptop sleeps, so the same check runs
  // when the tab comes back into view — before the person's first click.
  useEffect(() => {
    if (!token) return;

    const sessionUser = authService.getUser();
    const renewAt = () => authService.getSessionExpiresAt()
      - (authService.ownsRememberedSession() ? REMEMBERED_RENEW_AHEAD_MS : 0);

    const expire = async () => {
      // A renewed session stores a new token and expiry, which re-arms this
      // effect through the AUTH_CHANGE_EVENT resync.
      if (await authService.refreshTabSession()) return;
      // Over, not logged out: only this tab's session ends — a remembered
      // sign-in that could not be renewed just now (offline, rate-limited)
      // stays for the next visit.
      authService.endTabSession();
      authService.redirectToOwnLogin(sessionUser);
    };

    const checkOnReturn = () => {
      if (document.visibilityState === 'visible' && renewAt() <= Date.now()) expire();
    };
    document.addEventListener('visibilitychange', checkOnReturn);

    const timeUntilExpiration = renewAt() - Date.now();
    if (timeUntilExpiration <= 0) {
      console.log('[AUTH] Session already expired');
      expire();
      return () => document.removeEventListener('visibilitychange', checkOnReturn);
    }

    console.log(`[AUTH] Session expires in ${(timeUntilExpiration / (1000 * 60 * 60)).toFixed(1)} hours`);
    const timerId = setTimeout(expire, timeUntilExpiration);

    return () => {
      clearTimeout(timerId);
      document.removeEventListener('visibilitychange', checkOnReturn);
    };
  }, [token]);

  // Check if user has a specific role (using React state, not localStorage)
  const hasRole = useCallback(
    (role: 'super_admin' | 'group_admin' | 'admin' | 'user'): boolean => {
      if (!user?.role) return false;

      // Role hierarchy (higher number = more permissions)
      const roleHierarchy: Record<string, number> = {
        'super_admin': 4,
        'group_admin': 3,
        'admin': 2,
        'user': 1
      };

      const userRoleLevel = roleHierarchy[user.role] || 0;
      const requiredRoleLevel = roleHierarchy[role] || 0;

      return userRoleLevel >= requiredRoleLevel;
    },
    [user]
  );

  const isAuthenticated = !!token && !!user;

  // Compute role flags from user state (not from localStorage which may be stale)
  const isSuperAdmin = user?.role === 'super_admin';
  const isGroupAdmin = user?.role === 'group_admin' || user?.role === 'super_admin';
  const isAdmin = user?.role === 'admin' || user?.role === 'group_admin' || user?.role === 'super_admin';

  const updateUser = useCallback((updates: Partial<User>) => {
    setUser(prev => {
      if (!prev) return prev;
      const updated = { ...prev, ...updates };
      authSessionStore.setItem('user', JSON.stringify(updated));
      return updated;
    });
  }, []);

  return {
    user,
    token,
    tenantId,
    isAuthenticated,
    isLoading,
    isSuperAdmin,
    isGroupAdmin,
    isAdmin,
    login,
    logout,
    hasRole,
    updateUser,
  };
}
